import { useRef, useState } from 'react'
import {
    useFloatingNodeId,
    useFloating,
    offset,
    flip,
    shift,
    autoUpdate,
    useHover,
    safePolygon,
    useRole,
    useDismiss,
    useInteractions,
    FloatingNode,
    FloatingPortal,
    FloatingFocusManager,
    FloatingList,
    useListItem,
    useMergeRefs,
    useTransitionStatus,
} from '@floating-ui/react'
import styles from './TreeSelect.module.scss'
import ArrowIcon from '../assets/arrow-down.svg'
import { TreeSelectOption } from '../types'

interface Props {
    label: string
    value: string
    onChange: (value: string) => void
    options?: Omit<TreeSelectOption, 'children'>[]
}

export const SelectItem = ({ label, value, options = [], onChange }: Props) => {
    const [isOpen, setIsOpen] = useState(false)
    const nodeId = useFloatingNodeId()

    const elementsRef = useRef<Array<HTMLButtonElement | null>>([])
    const labelsRef = useRef<Array<string | null>>([])

    const item = useListItem()
    const isNested = !!options.length

    const { floatingStyles, refs, context } = useFloating<HTMLButtonElement>({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement: 'right-start',
        middleware: [offset({ mainAxis: 15 }), flip(), shift()],
        whileElementsMounted: autoUpdate,
    })

    const hover = useHover(context, {
        enabled: isNested,
        delay: { open: 75 },
        handleClose: safePolygon({ blockPointerEvents: true }),
    })

    const role = useRole(context, { role: 'menu' })
    const dismiss = useDismiss(context, { bubbles: true })

    const { isMounted, status } = useTransitionStatus(context)
    const { getReferenceProps, getFloatingProps } = useInteractions([hover, role, dismiss])

    const handleValueSelect = (value: string, haveNested: boolean = true): void => {
        if (haveNested) {
            return
        }

        onChange(value)
        setIsOpen(false)
    }

    return (
        <FloatingNode id={nodeId}>
            <button
                ref={useMergeRefs([refs.setReference, item.ref])}
                role="menuitem"
                className={styles.selectItem}
                onClick={() => handleValueSelect(value, isNested)}
                {...getReferenceProps()}
            >
                {label}
                {isNested && <ArrowIcon />}
            </button>
            <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
                {isMounted && (
                    <FloatingPortal>
                        <FloatingFocusManager context={context} modal={false}>
                            <div
                                ref={refs.setFloating}
                                className={styles.select}
                                style={floatingStyles}
                                data-status={status}
                                {...getFloatingProps()}
                            >
                                {options.map((props, index) => (
                                    <button
                                        key={index}
                                        role="menuitem"
                                        className={styles.selectItem}
                                        onClick={() => handleValueSelect(props.value, false)}
                                        {...getReferenceProps()}
                                    >
                                        {props.label}
                                    </button>
                                ))}
                            </div>
                        </FloatingFocusManager>
                    </FloatingPortal>
                )}
            </FloatingList>
        </FloatingNode>
    )
}
